'use client'

import type { ReactNode } from 'react'
import {
  cn,
  Field,
  FieldDescription,
  FieldLabel,
  Slider,
  ToggleGroup,
  ToggleGroupItem,
  TooltipIcon,
} from '@nwwp/ui-shadcn'
import { NumberInput } from '../../../controls/NumberInput'
import { parseCurrency } from '../../../utils/currency-format'

const LABEL = 'text-body-sm-bold text-gray-darker'
const VALUE = 'text-body-sm-bold tabular-nums text-gray-darker'

export function SliderField({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  display,
  hint,
  tooltip,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  onChange: (value: number) => void
  display: ReactNode
  hint?: ReactNode
  tooltip?: string
}) {
  return (
    <Field>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel className={LABEL}>
          {label}
          {tooltip && <TooltipIcon title={tooltip} aria-label={`${label} — more information`} />}
        </FieldLabel>
        <span className={VALUE}>{display}</span>
      </div>
      <Slider
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={[value]}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onValueChange={(values) => onChange(values[0]!)}
      />
      {hint && <FieldDescription className="text-subtitle text-neutral-dark">{hint}</FieldDescription>}
    </Field>
  )
}

export function MoneyField({
  label,
  value,
  onChange,
  hint,
  tooltip,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  hint?: ReactNode
  tooltip?: string
}) {
  return (
    <Field>
      <FieldLabel className={LABEL}>
        {label}
        {tooltip && <TooltipIcon title={tooltip} aria-label={`${label} — more information`} />}
      </FieldLabel>
      {hint && <FieldDescription className="text-subtitle text-neutral-dark">{hint}</FieldDescription>}
      <NumberInput
        aria-label={label}
        prefix="$"
        value={value}
        decimalScale={0}
        allowNegative={false}
        onChange={(e) => onChange(parseCurrency(e.target.value))}
        className="font-semibold text-gray-darker"
      />
    </Field>
  )
}

/**
 * Two- or three-way choice rendered as a segmented control.
 *
 * `ToggleGroup` allows deselecting the active item, which would leave the model without a
 * value, so an empty change is ignored rather than written back.
 */
export function SegmentedField<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
  tooltip,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  hint?: ReactNode
  tooltip?: string
}) {
  return (
    <Field>
      <FieldLabel className={LABEL}>
        {label}
        {tooltip && <TooltipIcon title={tooltip} aria-label={`${label} — more information`} />}
      </FieldLabel>
      <ToggleGroup
        type="single"
        variant="outline"
        value={value}
        onValueChange={(next) => next && onChange(next as T)}
        className="w-full"
        aria-label={label}
      >
        {options.map((option) => (
          <ToggleGroupItem key={option.value} value={option.value} className="flex-1 text-body-sm">
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {hint && <FieldDescription className="text-subtitle text-neutral-dark">{hint}</FieldDescription>}
    </Field>
  )
}

/** One label/value line in a results card. */
export function ResultRow({
  label,
  value,
  tooltip,
  tone = 'neutral',
  emphasis = false,
}: {
  label: ReactNode
  value: ReactNode
  tooltip?: string
  tone?: 'neutral' | 'positive' | 'negative'
  emphasis?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3.5 py-3',
        emphasis ? 'border-t-2 border-charcoal' : 'border-b border-border last:border-b-0'
      )}
    >
      <span className={cn('text-body-sm', emphasis ? 'text-gray-darker' : 'text-neutral-dark')}>
        {label}
        {tooltip && <TooltipIcon title={tooltip} aria-label="More information" />}
      </span>
      <span
        className={cn(
          'shrink-0 font-semibold tabular-nums',
          emphasis ? 'text-body-lg text-gray-darker' : 'text-body-sm',
          tone === 'positive' && 'text-green',
          tone === 'negative' && 'text-red',
          tone === 'neutral' && !emphasis && 'text-neutral-dark'
        )}
      >
        {value}
      </span>
    </div>
  )
}

export function ResultCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 tablet:p-7">
      <h2 className="text-title-sm text-gray-darker">{title}</h2>
      {subtitle && <p className="mt-1.5 mb-2 text-subtitle text-neutral-dark">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </div>
  )
}

/** Closing takeaway paragraph inside a results card, with a lead-in sentence. */
export function Takeaway({ headline, children }: { headline: string; children: ReactNode }) {
  return (
    <p className="mt-5 rounded-lg bg-bone-light p-4 text-body-sm text-neutral-dark">
      <strong className="mb-1.5 block font-semibold text-gray-darker">{headline}</strong>
      {children}
    </p>
  )
}
